// src/hooks/ai-hooks.ts

import React from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import { DEFAULT_QUERY_OPTIONS } from '../support/constants';
import {
  AiModelsResponse,
  ChatHistoryResponse,
  DriftIngestionStatus,
  DriftRunIngestResponse,
  DriftTaskStatus,
  DriftUploadResponse,
  ID,
  McpAuthConfig,
  McpCapabilityEvaluationPayload,
} from '../types/aiTypes';
import {
  apiDelete,
  apiGet,
  apiPostForm,
  apiPostJson,
  apiPutJson,
  toItems,
} from '../support/aiApi';
import { AI_MODEL_OPTIONS, DEFAULT_AI_MODEL_VALUE } from '../support/aiModelOptions';

export { toItems };

// -----------------------------------------------------------------------------
// Query Keys
// -----------------------------------------------------------------------------

export const aiKeys = {
  models: ['ai-models'] as const,

  chatHistory: (sessionId?: string | null) =>
    ['ai-chat-history', sessionId] as const,

  driftPipeline: ['drift-pipeline'] as const,

  driftTaskStatus: (taskId?: string | null) =>
    ['drift-task-status', taskId] as const,

  driftIngestionStatus: (executionId?: string | null) =>
    ['drift-ingestion-status', executionId] as const,

  mcpServers: ['db-mcp-servers'] as const,

  mcpServer: (serverId?: ID | null) =>
    ['db-mcp-server', serverId] as const,

  mcpAuth: (serverId?: ID | null) =>
    ['db-mcp-server-auth-admin', serverId] as const,

  mcpConfiguredCaps: (serverId?: ID | null) =>
    ['db-mcp-configured-capabilities', serverId] as const,

  mcpDiscoveredCaps: (serverId?: ID | null) =>
    ['db-mcp-discovered-capabilities', serverId] as const,

  mcpHealth: (serverId?: ID | null) =>
    ['db-mcp-server-health', serverId] as const,

  mcpSync: (serverId?: ID | null) =>
    ['db-mcp-server-sync-state', serverId] as const,

  // --- Agent registry ---
  agents: ['db-agents'] as const,

  agent: (agentId?: ID | null) =>
    ['db-agent', agentId] as const,

  agentPromptVersions: (agentId?: ID | null) =>
    ['db-agent-prompt-versions', agentId] as const,

  agentCapabilityScopes: (agentId?: ID | null) =>
    ['db-agent-capability-scopes', agentId] as const,

  agentContextProviders: (agentId?: ID | null) =>
    ['db-agent-context-providers', agentId] as const,

  agentRoutingRules: (orchestratorId?: ID | null) =>
    ['db-agent-routing-rules', orchestratorId] as const,

  agentA2AConfig: (agentId?: ID | null) =>
    ['db-agent-a2a-config', agentId] as const,

  agentEndpointScopes: (agentId?: ID | null) =>
    ['db-agent-endpoint-scopes', agentId] as const,

  agentInvocationAudits: (agentId?: ID | null) =>
    ['db-agent-invocation-audits', agentId] as const,

  agentA2AEndpoints: ['db-agent-a2a-endpoints'] as const,

  documentationEntityTypes: ['db-documentation-entity-types'] as const,
};

// -----------------------------------------------------------------------------
// Local Chat Sessions
// -----------------------------------------------------------------------------

export type LocalChatSession = {
  session_id: string;
  title?: string | null;
  updated_at: string;
  created_at?: string;
  message_count?: number;
};

const LS_KEY = 'toolbox_ai_chat_sessions_v2';

function loadLocalSessions(): LocalChatSession[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];

    const data = JSON.parse(raw);
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

function saveLocalSessions(sessions: LocalChatSession[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(sessions.slice(0, 50)));
}

export const useLocalChatSessions = () => {
  const [sessions, setSessions] = React.useState<LocalChatSession[]>(() =>
    loadLocalSessions()
  );

  const upsertSession = React.useCallback(
    (session_id: string, title?: string | null) => {
      setSessions((prev) => {
        const now = new Date().toISOString();
        const existing = prev.find((s) => s.session_id === session_id);
        const withoutCurrent = prev.filter((s) => s.session_id !== session_id);

        const next = [
          {
            ...existing,
            session_id,
            title: title ?? existing?.title ?? 'New chat',
            created_at: existing?.created_at || now,
            updated_at: now,
          },
          ...withoutCurrent,
        ];

        saveLocalSessions(next);
        return next.slice(0, 50);
      });
    },
    []
  );

  const deleteSession = React.useCallback((session_id: string) => {
    setSessions((prev) => {
      const next = prev.filter((s) => s.session_id !== session_id);
      saveLocalSessions(next);
      return next;
    });
  }, []);

  const refresh = React.useCallback(() => {
    setSessions(loadLocalSessions());
  }, []);

  return {
    sessions,
    upsertSession,
    deleteSession,
    refresh,
  };
};

// -----------------------------------------------------------------------------
// Models
// -----------------------------------------------------------------------------

export const useFetchAiModels = (options?: any) =>
  useQuery<AiModelsResponse>({
    queryKey: aiKeys.models,
    queryFn: async () => ({
      default: DEFAULT_AI_MODEL_VALUE,
      models: AI_MODEL_OPTIONS.map((model) => ({
        id: model.value,
        name: model.label,
        value: model.value,
        label: model.label,
        tier: model.tier,
        requests_per_minute: model.requestsPerMinute,
        peak_active_tokens_per_minute: model.peakActiveTokensPerMinute,
        monthly_input_token_cost: model.monthlyInputTokenCost,
        monthly_completion_token_cost: model.monthlyCompletionTokenCost,
        quarterly_cost: model.quarterlyCost,
        notes: model.notes,
      })),
    }),
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        ...(options || {}),
      },
  });
// -----------------------------------------------------------------------------
// Chat
// -----------------------------------------------------------------------------

export const useFetchChatSessionMessages = (
  sessionId?: string | null,
  options?: any
) =>
  useQuery<ChatHistoryResponse>({
    queryKey: aiKeys.chatHistory(sessionId),
    queryFn: () => apiGet<ChatHistoryResponse>('ai/history/', { session_id: sessionId }),
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        ...(options || {}),
        enabled: !!sessionId && ((options && options.enabled) ?? true),
      },
  });

// -----------------------------------------------------------------------------
// DRIFT
// -----------------------------------------------------------------------------

export const useFetchDriftPipeline = (options?: any) =>
  useQuery<any>({
    queryKey: aiKeys.driftPipeline,
    queryFn: () => apiGet<any>('ai/drift/pipeline/'),
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        ...(options || {}),
        enabled: (options && options.enabled) ?? true,
      },
  });

export const useDriftUploadFilesMutation = () =>
  useMutation<DriftUploadResponse, Error, { files: File[] }>({
    mutationFn: async (payload) => {
      if (!payload.files?.length) throw new Error('No files selected');

      const form = new FormData();

      for (const file of payload.files) {
        form.append('files', file);
      }

      return apiPostForm<DriftUploadResponse>('ai/drift/upload/', form);
    },
  });

export const useFetchDriftTaskStatus = (
  taskId?: string | null,
  options?: any
) =>
  useQuery<DriftTaskStatus>({
    queryKey: aiKeys.driftTaskStatus(taskId),
    queryFn: () =>
      apiGet<DriftTaskStatus>(
        `ai/drift/status/${encodeURIComponent(String(taskId))}/`
      ),
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        ...(options || {}),
        enabled: !!taskId && ((options && options.enabled) ?? true),
        refetchInterval: (options && options.refetchInterval) ?? 3000,
      },
  });

export const useDriftRunIngestionMutation = () =>
  useMutation<DriftRunIngestResponse, Error, void>({
    mutationFn: () => apiPostJson<DriftRunIngestResponse>('ai/drift/ingest/', {}),
  });

export const useFetchDriftIngestionStatus = (
  executionId?: string | null,
  options?: any
) =>
  useQuery<DriftIngestionStatus>({
    queryKey: aiKeys.driftIngestionStatus(executionId),
    queryFn: () =>
      apiGet<DriftIngestionStatus>(
        `ai/drift/ingestion/${encodeURIComponent(String(executionId))}/`
      ),
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        ...(options || {}),
        enabled: !!executionId && ((options && options.enabled) ?? true),
        refetchInterval: (options && options.refetchInterval) ?? 5000,
      },
  });

export const useDriftDeletePipelineFileMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    any,
    Error,
    { pipeline_id?: string; filename: string; delete_vectors?: boolean }
  >({
    mutationFn: async (payload) => {
      if (!payload.filename) throw new Error('filename is required');

      return apiDelete<any>('ai/drift/pipeline/file/', {
        pipeline_id: payload.pipeline_id,
        filename: payload.filename,
        delete_vectors: payload.delete_vectors ? 'true' : 'false',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiKeys.driftPipeline });
    },
  });
};

// -----------------------------------------------------------------------------
// MCP Servers
// -----------------------------------------------------------------------------

export const useFetchMcpServers = (options?: any) =>
  useQuery<any>({
    queryKey: aiKeys.mcpServers,
    queryFn: () =>
      apiGet<any>('db/mcp-server/', {
        page: 1,
        size: 500,
        detailed: true,
      }),
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        ...(options || {}),
        enabled: (options && options.enabled) ?? true,
        refetchInterval: (options && options.refetchInterval) ?? 15000,
      },
  });

export const useFetchMcpServer = (
  serverId?: ID | null,
  options?: any
) =>
  useQuery<any>({
    queryKey: aiKeys.mcpServer(serverId),
    queryFn: () => apiGet<any>(`db/mcp-server/${serverId}/`, { detailed: true }),
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        ...(options || {}),
        enabled: !!serverId && ((options && options.enabled) ?? true),
      },
  });

export const useCreateMcpServerMutation = () =>
  useMutation<any, Error, any>({
    mutationFn: (payload) => apiPostJson<any>('db/mcp-server/', payload),
  });

export const useUpdateMcpServerMutation = () =>
  useMutation<any, Error, any>({
    mutationFn: (payload) => {
      const id = payload?.id;
      if (!id) throw new Error('Server id is required');

      return apiPutJson<any>(`db/mcp-server/${id}/`, payload);
    },
  });

export const useDeleteMcpServerMutation = () =>
  useMutation<any, Error, ID>({
    mutationFn: (serverId) => apiDelete<any>(`db/mcp-server/${serverId}/`),
  });

// -----------------------------------------------------------------------------
// MCP Auth
// -----------------------------------------------------------------------------

export const useFetchMcpServerAuthConfigs = (
  serverId?: ID | null,
  options?: any
) =>
  useQuery<any>({
    queryKey: aiKeys.mcpAuth(serverId),
    queryFn: () =>
      apiGet<any>('db/mcp-server-auth-admin/', {
        mcp_server: serverId,
      }),
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        ...(options || {}),
        enabled: !!serverId && ((options && options.enabled) ?? true),
      },
  });

export const useCreateMcpServerAuthMutation = () =>
  useMutation<any, Error, McpAuthConfig>({
    mutationFn: (payload) => apiPostJson<any>('db/mcp-server-auth-admin/', payload),
  });

export const useUpdateMcpServerAuthMutation = () =>
  useMutation<any, Error, McpAuthConfig & { id: ID }>({
    mutationFn: (payload) => {
      if (!payload?.id) throw new Error('Auth config id is required');

      return apiPutJson<any>(`db/mcp-server-auth-admin/${payload.id}/`, payload);
    },
  });

export const useDeleteMcpServerAuthMutation = () =>
  useMutation<any, Error, ID>({
    mutationFn: (id) => apiDelete<any>(`db/mcp-server-auth-admin/${id}/`),
  });

// -----------------------------------------------------------------------------
// MCP Capabilities
// -----------------------------------------------------------------------------

export const useFetchMcpConfiguredCapabilities = (
  serverId?: ID | null,
  options?: any
) =>
  useQuery<any>({
    queryKey: aiKeys.mcpConfiguredCaps(serverId),
    queryFn: () =>
      apiGet<any>('db/mcp-server-capability/', {
        mcp_server: serverId,
        page: 1,
        size: 1000,
        detailed: true,
      }),
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        ...(options || {}),
        enabled: !!serverId && ((options && options.enabled) ?? true),
        refetchInterval: (options && options.refetchInterval) ?? 15000,
      },
  });

export const useCreateMcpCapabilityMutation = () =>
  useMutation<any, Error, any>({
    mutationFn: (payload) => apiPostJson<any>('db/mcp-server-capability/', payload),
  });

export const useUpdateMcpCapabilityMutation = () =>
  useMutation<any, Error, any>({
    mutationFn: (payload) => {
      const id = payload?.id;
      if (!id) throw new Error('Capability id is required');

      return apiPutJson<any>(`db/mcp-server-capability/${id}/`, payload);
    },
  });

export const useDeleteMcpCapabilityMutation = () =>
  useMutation<any, Error, ID>({
    mutationFn: (id) => apiDelete<any>(`db/mcp-server-capability/${id}/`),
  });

export const useFetchMcpDiscoveredCapabilities = (
  serverId?: ID | null,
  options?: any
) =>
  useQuery<any>({
    queryKey: aiKeys.mcpDiscoveredCaps(serverId),
    queryFn: () =>
      apiGet<any>('db/mcp-server-discovered-capability/', {
        mcp_server: serverId,
        page: 1,
        size: 1000,
        detailed: true,
      }),
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        ...(options || {}),
        enabled: !!serverId && ((options && options.enabled) ?? true),
        refetchInterval: (options && options.refetchInterval) ?? 15000,
      },
  });

// -----------------------------------------------------------------------------
// MCP Health / Sync
// -----------------------------------------------------------------------------

export const useFetchMcpServerHealthRows = (
  serverId?: ID | null,
  options?: any
) =>
  useQuery<any>({
    queryKey: aiKeys.mcpHealth(serverId),
    queryFn: () =>
      apiGet<any>('db/mcp-server-health/', {
        mcp_server: serverId,
        page: 1,
        size: 50,
        detailed: true,
        'order-by': '-last_check_at',
      }),
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        ...(options || {}),
        enabled: !!serverId && ((options && options.enabled) ?? true),
        refetchInterval: (options && options.refetchInterval) ?? 15000,
      },
  });

export const useFetchMcpServerSyncState = (
  serverId?: ID | null,
  options?: any
) =>
  useQuery<any>({
    queryKey: aiKeys.mcpSync(serverId),
    queryFn: () =>
      apiGet<any>('db/mcp-server-sync-state/', {
        mcp_server: serverId,
        page: 1,
        size: 10,
        detailed: true,
      }),
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        ...(options || {}),
        enabled: !!serverId && ((options && options.enabled) ?? true),
        refetchInterval: (options && options.refetchInterval) ?? 15000,
      },
  });

// -----------------------------------------------------------------------------
// MCP Actions
// -----------------------------------------------------------------------------

export const useTriggerMcpDiscoverySyncMutation = () =>
  useMutation<any, Error, ID>({
    mutationFn: (serverId) => {
      if (!serverId) throw new Error('serverId is required');

      return apiPostJson<any>('ai/mcp/discovery/sync/', {
        server_id: serverId,
      });
    },
  });

export const useEvaluateMcpCapabilityMutation = () =>
  useMutation<any, Error, McpCapabilityEvaluationPayload>({
    mutationFn: (payload) => apiPostJson<any>('ai/mcp/capability/evaluate/', payload),
  });

// -----------------------------------------------------------------------------
// Agent Registry — Definitions
// -----------------------------------------------------------------------------

export const useFetchAgents = (options?: any) =>
  useQuery<any>({
    queryKey: aiKeys.agents,
    queryFn: () =>
      apiGet<any>('db/agent-definition/', {
        page: 1,
        size: 500,
        detailed: true,
      }),
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        ...(options || {}),
        enabled: (options && options.enabled) ?? true,
      },
  });

export const useFetchAgent = (agentId?: ID | null, options?: any) =>
  useQuery<any>({
    queryKey: aiKeys.agent(agentId),
    queryFn: () => apiGet<any>(`db/agent-definition/${agentId}/`, { detailed: true }),
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        ...(options || {}),
        enabled: !!agentId && ((options && options.enabled) ?? true),
      },
  });

export const useCreateAgentMutation = () =>
  useMutation<any, Error, any>({
    mutationFn: (payload) => apiPostJson<any>('db/agent-definition/', payload),
  });

export const useUpdateAgentMutation = () =>
  useMutation<any, Error, any>({
    mutationFn: (payload) => {
      const id = payload?.id;
      if (!id) throw new Error('Agent id is required');

      return apiPutJson<any>(`db/agent-definition/${id}/`, payload);
    },
  });

export const useDeleteAgentMutation = () =>
  useMutation<any, Error, ID>({
    mutationFn: (agentId) => apiDelete<any>(`db/agent-definition/${agentId}/`),
  });

// -----------------------------------------------------------------------------
// Agent Registry — Prompt Versions
// -----------------------------------------------------------------------------

export const useFetchAgentPromptVersions = (
  agentId?: ID | null,
  options?: any
) =>
  useQuery<any>({
    queryKey: aiKeys.agentPromptVersions(agentId),
    queryFn: () =>
      apiGet<any>('db/agent-prompt-version/', {
        agent: agentId,
        page: 1,
        size: 500,
        detailed: true,
      }),
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        ...(options || {}),
        enabled: !!agentId && ((options && options.enabled) ?? true),
      },
  });

export const useCreateAgentPromptVersionMutation = () =>
  useMutation<any, Error, any>({
    mutationFn: (payload) => apiPostJson<any>('db/agent-prompt-version/', payload),
  });

export const useUpdateAgentPromptVersionMutation = () =>
  useMutation<any, Error, any>({
    mutationFn: (payload) => {
      const id = payload?.id;
      if (!id) throw new Error('Prompt version id is required');

      return apiPutJson<any>(`db/agent-prompt-version/${id}/`, payload);
    },
  });

export const useDeleteAgentPromptVersionMutation = () =>
  useMutation<any, Error, ID>({
    mutationFn: (id) => apiDelete<any>(`db/agent-prompt-version/${id}/`),
  });

// -----------------------------------------------------------------------------
// Agent Registry — Capability Scopes
// -----------------------------------------------------------------------------

export const useFetchAgentCapabilityScopes = (
  agentId?: ID | null,
  options?: any
) =>
  useQuery<any>({
    queryKey: aiKeys.agentCapabilityScopes(agentId),
    queryFn: () =>
      apiGet<any>('db/agent-capability-scope/', {
        agent: agentId,
        page: 1,
        size: 500,
        detailed: true,
      }),
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        ...(options || {}),
        enabled: !!agentId && ((options && options.enabled) ?? true),
      },
  });

export const useCreateAgentCapabilityScopeMutation = () =>
  useMutation<any, Error, any>({
    mutationFn: (payload) => apiPostJson<any>('db/agent-capability-scope/', payload),
  });

export const useUpdateAgentCapabilityScopeMutation = () =>
  useMutation<any, Error, any>({
    mutationFn: (payload) => {
      const id = payload?.id;
      if (!id) throw new Error('Capability scope id is required');

      return apiPutJson<any>(`db/agent-capability-scope/${id}/`, payload);
    },
  });

export const useDeleteAgentCapabilityScopeMutation = () =>
  useMutation<any, Error, ID>({
    mutationFn: (id) => apiDelete<any>(`db/agent-capability-scope/${id}/`),
  });

// -----------------------------------------------------------------------------
// Agent Registry — Context Providers
// -----------------------------------------------------------------------------

export const useFetchAgentContextProviders = (
  agentId?: ID | null,
  options?: any
) =>
  useQuery<any>({
    queryKey: aiKeys.agentContextProviders(agentId),
    queryFn: () =>
      apiGet<any>('db/agent-context-provider/', {
        agent: agentId,
        page: 1,
        size: 500,
        detailed: true,
      }),
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        ...(options || {}),
        enabled: !!agentId && ((options && options.enabled) ?? true),
      },
  });

export const useCreateAgentContextProviderMutation = () =>
  useMutation<any, Error, any>({
    mutationFn: (payload) => apiPostJson<any>('db/agent-context-provider/', payload),
  });

export const useUpdateAgentContextProviderMutation = () =>
  useMutation<any, Error, any>({
    mutationFn: (payload) => {
      const id = payload?.id;
      if (!id) throw new Error('Context provider id is required');

      return apiPutJson<any>(`db/agent-context-provider/${id}/`, payload);
    },
  });

export const useDeleteAgentContextProviderMutation = () =>
  useMutation<any, Error, ID>({
    mutationFn: (id) => apiDelete<any>(`db/agent-context-provider/${id}/`),
  });

// -----------------------------------------------------------------------------
// Agent Registry — Routing Rules (outgoing, keyed by orchestrator agent)
// -----------------------------------------------------------------------------

export const useFetchAgentRoutingRules = (
  orchestratorId?: ID | null,
  options?: any
) =>
  useQuery<any>({
    queryKey: aiKeys.agentRoutingRules(orchestratorId),
    queryFn: () =>
      apiGet<any>('db/agent-routing-rule/', {
        orchestrator: orchestratorId,
        page: 1,
        size: 500,
        detailed: true,
      }),
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        ...(options || {}),
        enabled: !!orchestratorId && ((options && options.enabled) ?? true),
      },
  });

export const useCreateAgentRoutingRuleMutation = () =>
  useMutation<any, Error, any>({
    mutationFn: (payload) => apiPostJson<any>('db/agent-routing-rule/', payload),
  });

export const useUpdateAgentRoutingRuleMutation = () =>
  useMutation<any, Error, any>({
    mutationFn: (payload) => {
      const id = payload?.id;
      if (!id) throw new Error('Routing rule id is required');

      return apiPutJson<any>(`db/agent-routing-rule/${id}/`, payload);
    },
  });

export const useDeleteAgentRoutingRuleMutation = () =>
  useMutation<any, Error, ID>({
    mutationFn: (id) => apiDelete<any>(`db/agent-routing-rule/${id}/`),
  });

// -----------------------------------------------------------------------------
// Agent Registry — A2A Config (one per agent)
// -----------------------------------------------------------------------------

export const useFetchAgentA2AConfig = (agentId?: ID | null, options?: any) =>
  useQuery<any>({
    queryKey: aiKeys.agentA2AConfig(agentId),
    queryFn: () =>
      apiGet<any>('db/agent-a2a-config/', {
        agent: agentId,
        page: 1,
        size: 10,
        detailed: true,
      }),
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        ...(options || {}),
        enabled: !!agentId && ((options && options.enabled) ?? true),
      },
  });

export const useCreateAgentA2AConfigMutation = () =>
  useMutation<any, Error, any>({
    mutationFn: (payload) => apiPostJson<any>('db/agent-a2a-config/', payload),
  });

export const useUpdateAgentA2AConfigMutation = () =>
  useMutation<any, Error, any>({
    mutationFn: (payload) => {
      const id = payload?.id;
      if (!id) throw new Error('A2A config id is required');

      return apiPutJson<any>(`db/agent-a2a-config/${id}/`, payload);
    },
  });

export const useDeleteAgentA2AConfigMutation = () =>
  useMutation<any, Error, ID>({
    mutationFn: (id) => apiDelete<any>(`db/agent-a2a-config/${id}/`),
  });

// -----------------------------------------------------------------------------
// Agent Registry — A2A Endpoint Scopes (per agent)
// -----------------------------------------------------------------------------

export const useFetchAgentEndpointScopes = (
  agentId?: ID | null,
  options?: any
) =>
  useQuery<any>({
    queryKey: aiKeys.agentEndpointScopes(agentId),
    queryFn: () =>
      apiGet<any>('db/agent-a2a-endpoint-scope/', {
        agent: agentId,
        page: 1,
        size: 500,
        detailed: true,
      }),
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        ...(options || {}),
        enabled: !!agentId && ((options && options.enabled) ?? true),
      },
  });

export const useCreateAgentEndpointScopeMutation = () =>
  useMutation<any, Error, any>({
    mutationFn: (payload) => apiPostJson<any>('db/agent-a2a-endpoint-scope/', payload),
  });

export const useUpdateAgentEndpointScopeMutation = () =>
  useMutation<any, Error, any>({
    mutationFn: (payload) => {
      const id = payload?.id;
      if (!id) throw new Error('Endpoint scope id is required');

      return apiPutJson<any>(`db/agent-a2a-endpoint-scope/${id}/`, payload);
    },
  });

export const useDeleteAgentEndpointScopeMutation = () =>
  useMutation<any, Error, ID>({
    mutationFn: (id) => apiDelete<any>(`db/agent-a2a-endpoint-scope/${id}/`),
  });

// -----------------------------------------------------------------------------
// Agent Registry — Invocation Audit (read-only)
// -----------------------------------------------------------------------------

export const useFetchAgentInvocationAudits = (
  agentId?: ID | null,
  options?: any
) =>
  useQuery<any>({
    queryKey: aiKeys.agentInvocationAudits(agentId),
    queryFn: () =>
      apiGet<any>('db/agent-invocation-audit/', {
        agent: agentId,
        page: 1,
        size: 100,
        detailed: true,
        'order-by': '-started_at',
      }),
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        ...(options || {}),
        enabled: !!agentId && ((options && options.enabled) ?? true),
      },
  });

// -----------------------------------------------------------------------------
// Agent Registry — A2A Endpoints (global registry)
// -----------------------------------------------------------------------------

export const useFetchAgentA2AEndpoints = (options?: any) =>
  useQuery<any>({
    queryKey: aiKeys.agentA2AEndpoints,
    queryFn: () =>
      apiGet<any>('db/agent-a2a-endpoint/', {
        page: 1,
        size: 500,
        detailed: true,
      }),
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        ...(options || {}),
        enabled: (options && options.enabled) ?? true,
      },
  });

export const useCreateAgentA2AEndpointMutation = () =>
  useMutation<any, Error, any>({
    mutationFn: (payload) => apiPostJson<any>('db/agent-a2a-endpoint/', payload),
  });

export const useUpdateAgentA2AEndpointMutation = () =>
  useMutation<any, Error, any>({
    mutationFn: (payload) => {
      const id = payload?.id;
      if (!id) throw new Error('Endpoint id is required');

      return apiPutJson<any>(`db/agent-a2a-endpoint/${id}/`, payload);
    },
  });

export const useDeleteAgentA2AEndpointMutation = () =>
  useMutation<any, Error, ID>({
    mutationFn: (id) => apiDelete<any>(`db/agent-a2a-endpoint/${id}/`),
  });

// -----------------------------------------------------------------------------
// Documentation Entity Types (context-provider target picker)
// -----------------------------------------------------------------------------

export const useFetchDocumentationEntityTypes = (options?: any) =>
  useQuery<any>({
    queryKey: aiKeys.documentationEntityTypes,
    queryFn: () =>
      apiGet<any>('db/documentation-entity-type/', {
        page: 1,
        size: 500,
      }),
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        ...(options || {}),
        enabled: (options && options.enabled) ?? true,
      },
  });